// InboxSwitcher — top-nav chip that displays the current inbox + lets
// the user switch to another accessible inbox.
//
// Sits in TopNav. Mounted ABOVE the InboxScopeProvider (which lives
// inside the routed Outlet) so it can't useContext directly — it reads
// the slug from useLocation() and looks up the inbox via the cached
// useGmailInboxes() hook instead. On switch, it preserves the
// current sub-view (e.g. /dashboard, /activity) when that sub-view is
// inbox-scoped; otherwise lands the user on /my-day inside the new inbox.

import { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronsUpDown, Check, Inbox as InboxIcon, Lock, Settings2 } from 'lucide-react';
import { useInboxNewCounts, type GmailInbox } from '@/lib/queries';
import { useViewableInboxes } from '@/lib/use-access';
import {
  buildInboxPath,
  isInboxScopedSubPath,
  parseInboxPath,
  readLastVisited,
} from '@/lib/inbox-scope';
import { cn } from '@/lib/utils';
import { inboxBadgeClasses } from '@/lib/inbox-colors';

export function InboxSwitcher({
  variant = 'chip',
  onNavigate,
}: {
  /** 'chip' = compact top-bar dropdown (desktop). 'list' = flat, one-tap
   *  tappable rows for the mobile nav drawer — no nested dropdown. */
  variant?: 'chip' | 'list';
  /** Called after a switch / "Manage inboxes" nav — lets the mobile drawer close. */
  onNavigate?: () => void;
} = {}) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // Force re-render when setLastVisitedSlug() updates localStorage within
  // the same tab (cross-tab changes use the native 'storage' event, but
  // same-tab changes need the custom event).
  const [, setSlugTick] = useState(0);
  useEffect(() => {
    const handler = () => setSlugTick((n) => n + 1);
    window.addEventListener('pa:inbox-slug-changed', handler);
    return () => window.removeEventListener('pa:inbox-slug-changed', handler);
  }, []);
  // Inboxes this person can open (tickets.view, migration 078). An Owner or
  // Admin has no per-inbox grant rows but holds every inbox, so this reads
  // the same my_access answer the rest of the app uses.
  const inboxesQ = useViewableInboxes();
  const inboxes = useMemo(() => inboxesQ.data.filter((i) => i.active), [inboxesQ.data]);

  // New-ticket counts per inbox → badges in the dropdown + a total on the
  // trigger for new tickets in OTHER inboxes (the "switch to see new work"
  // signal). RLS scopes the map to accessible inboxes.
  const newCounts = useInboxNewCounts().data;

  const { slug: pathSlug, subPath } = parseInboxPath(pathname);
  // When the URL is unscoped (e.g. /tickets/:id, /settings, /reports)
  // there's no slug to read. Fall back to the user's last-visited inbox
  // so the switcher still reflects the user's mental "context" — and
  // ticket-detail pages stamp lastVisited to the ticket's own inbox so
  // even a cold deep-link onto a ticket shows the right chip.
  const effectiveSlug = pathSlug ?? readLastVisited();
  const currentInbox = useMemo(
    () => inboxes.find((i) => i.label_namespace === effectiveSlug) ?? null,
    [inboxes, effectiveSlug],
  );

  // If the URL isn't inbox-scoped (e.g. /settings, /kb), the switcher
  // shows a placeholder + still lets the user jump into an inbox.
  const label = currentInbox?.display_name ?? 'Pick inbox';

  // Total new tickets sitting in inboxes the user ISN'T currently viewing —
  // shown on the trigger so they know to switch without opening the menu.
  const otherNewTotal = useMemo(() => {
    if (!newCounts) return 0;
    let n = 0;
    for (const ib of inboxes) {
      if (ib.id !== currentInbox?.id) n += newCounts.get(ib.id) ?? 0;
    }
    return n;
  }, [newCounts, inboxes, currentInbox]);

  function switchTo(target: GmailInbox) {
    // Preserve the current sub-view across the switch — but only if
    // it's a sub-view that exists inside /inbox/:slug. Otherwise (e.g.
    // user clicked from /settings) land them on my-day.
    const targetSub = isInboxScopedSubPath(subPath) ? subPath : 'my-day';
    // Preserve query params (e.g. ?email= on contacts/view) across the switch.
    const qs = targetSub === subPath ? search : '';
    navigate(buildInboxPath(target.label_namespace, targetSub) + qs);
    onNavigate?.();
  }

  // Nothing useful to render until inbox list loads. Don't show a
  // skeleton-shaped chip — too distracting at this size. Just render
  // a static placeholder that flips to real content on load.
  if (inboxesQ.isLoading) {
    if (variant === 'list') {
      return <p className="px-3 py-2 text-sm text-muted-foreground">Loading inboxes…</p>;
    }
    return (
      <span className="inline-flex h-7 items-center gap-1 rounded-md border border-border/60 bg-background/60 px-2 text-xs text-muted-foreground">
        <InboxIcon className="h-3.5 w-3.5" /> Loading…
      </span>
    );
  }
  if (inboxes.length === 0) {
    return null;
  }

  // Mobile drawer: a flat list of tappable inbox rows — one tap switches +
  // closes the drawer (via onNavigate). Avoids a dropdown-inside-a-drawer.
  if (variant === 'list') {
    return (
      <div className="flex flex-col gap-0.5">
        {inboxes.map((ib) => {
          const isCurrent = ib.id === currentInbox?.id;
          const newCount = newCounts?.get(ib.id) ?? 0;
          return (
            <button
              key={ib.id}
              type="button"
              onClick={() => switchTo(ib)}
              aria-current={isCurrent ? 'true' : undefined}
              className={cn(
                'flex items-center gap-2.5 rounded-md px-3 py-2.5 text-left text-sm transition-colors',
                isCurrent
                  ? 'bg-accent font-medium text-foreground'
                  : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
              )}
            >
              <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset', inboxBadgeClasses(ib))} />
              <span className="min-w-0 flex-1 truncate">{ib.display_name}</span>
              {ib.is_confidential ? <Lock className="h-3.5 w-3.5 shrink-0 text-tint-critical-fg" /> : null}
              {newCount > 0 ? (
                <span
                  className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-tint-info-fg px-1.5 text-2xs font-semibold leading-none text-background"
                  title={`${newCount} new ticket${newCount === 1 ? '' : 's'}`}
                >
                  {newCount > 99 ? '99+' : newCount}
                </span>
              ) : null}
              {isCurrent ? <Check className="h-4 w-4 shrink-0 text-foreground" /> : null}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          // The trigger used to wear the inbox's full colour chip — a
          // saturated fill, ring and text. In the top nav that made it the
          // loudest element on the bar, louder than the active nav item, which
          // inverts the hierarchy: navigation is the primary job, the current
          // inbox is context.
          //
          // It now uses the same treatment as the rows inside its own dropdown
          // (see the list below): a neutral chip with the inbox colour carried
          // by a small dot. Identity is still instantly readable, at a
          // fraction of the visual weight.
          className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-md border border-border/60 bg-background/60 px-2 text-xs font-medium text-foreground transition-colors',
            'hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
          title="Switch inbox"
        >
          {currentInbox ? (
            <span
              aria-hidden
              className={cn(
                'h-2 w-2 shrink-0 rounded-full ring-1 ring-inset',
                inboxBadgeClasses(currentInbox),
              )}
            />
          ) : (
            <InboxIcon className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          {/* Shorter below xl so a long inbox name can't crowd the nav entries. */}
          <span className="max-w-[100px] truncate xl:max-w-[140px]">{label}</span>
          {currentInbox?.is_confidential ? <Lock className="h-3 w-3 text-tint-critical-fg" /> : null}
          {otherNewTotal > 0 ? (
            <span
              className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-tint-info-fg px-1 text-3xs font-semibold leading-none text-background"
              title={`${otherNewTotal} new ticket${otherNewTotal === 1 ? '' : 's'} in other inboxes`}
            >
              {otherNewTotal > 99 ? '99+' : otherNewTotal}
            </span>
          ) : null}
          <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-3xs uppercase tracking-wide text-muted-foreground">
          Switch inbox
        </DropdownMenuLabel>
        {inboxes.map((ib) => {
          const isCurrent = ib.id === currentInbox?.id;
          const newCount = newCounts?.get(ib.id) ?? 0;
          return (
            <DropdownMenuItem
              key={ib.id}
              onClick={() => switchTo(ib)}
              className={cn('flex items-center gap-2', isCurrent && 'bg-accent/40')}
            >
              <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset', inboxBadgeClasses(ib))} />
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1 truncate text-sm font-medium">
                  {ib.display_name}
                  {ib.is_confidential ? <Lock className="h-3 w-3 text-tint-critical-fg" /> : null}
                  {ib.is_primary ? (
                    <span className="rounded bg-tint-neutral px-1 text-3xs uppercase tracking-wide text-tint-neutral-fg">
                      Primary
                    </span>
                  ) : null}
                </p>
                <p className="truncate text-2xs text-muted-foreground">{ib.email_address}</p>
              </div>
              {newCount > 0 ? (
                <span
                  className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-tint-info-fg px-1.5 text-2xs font-semibold leading-none text-background"
                  title={`${newCount} new ticket${newCount === 1 ? '' : 's'}`}
                >
                  {newCount > 99 ? '99+' : newCount}
                </span>
              ) : null}
              {isCurrent ? <Check className="h-3.5 w-3.5 shrink-0 text-foreground" /> : null}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/inboxes')} className="gap-2 text-xs">
          <Settings2 className="h-3.5 w-3.5" /> Manage inboxes
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
